import { MutationCache, QueryCache, QueryClient } from "@tanstack/react-query";
import { ApiError, parseApiError } from "../utils/parseApiError";
import { displayError } from "../utils/showToastNotification";

/**
 * Global TanStack Query client.
 *
 * Defaults are picked for an app that talks to a single REST backend
 * the user is logged into:
 *
 *   - `staleTime: 60_000` — Most reads don't change minute-to-minute.
 *     Caching for 60s eliminates the burst of duplicate fetches when
 *     multiple components mount the same query (e.g., logos used
 *     across feed cards).
 *   - `gcTime: 5 * 60_000` — Keep cached data for 5 min after the last
 *     observer unmounts. Navigating back to a page within that window
 *     shows cached data instantly while a background refetch runs.
 *   - `refetchOnWindowFocus: false` — Re-fetching on every tab focus
 *     is jarring on the feed page and rarely matches what the user
 *     wants. Individual queries can opt in if needed.
 *   - `retry`: smart default — never retry 4xx (the request was
 *     correct and the server says no), retry once on 5xx / network.
 *   - Mutations DO NOT retry by default — re-sending a "delete ticket"
 *     because of a hiccup is worse than failing visibly.
 */
function shouldRetry(failureCount: number, error: unknown): boolean {
  if (failureCount >= 1) return false;
  if (error instanceof ApiError) {
    // 4xx is the server explicitly rejecting — retrying won't change
    // anything except waste battery and bandwidth.
    if (error.statusCode >= 400 && error.statusCode < 500) return false;
  }
  return true;
}

/**
 * Should a failure produce a toast? We swallow:
 *   - 401: the axios interceptor handles refresh / logout — toasting
 *     here causes "session expired" spam during normal token rotation.
 *   - meta.suppressErrorToast: opt-out for hooks that want to handle
 *     errors inline (e.g., a form that renders fieldErrors).
 */
function shouldToast(error: unknown, meta: Record<string, unknown> | undefined): boolean {
  if (meta?.["suppressErrorToast"]) return false;
  const apiError = parseApiError(error);
  if (apiError.statusCode === 401) return false;
  return true;
}

/**
 * Log the failure with its backend `X-Request-Id` (finding M3) when one
 * is present, so a user-reported error can be traced to the exact
 * server-side request. Kept out of the toast — it's diagnostic, not copy.
 */
function logRequestId(error: unknown): void {
  const apiError = parseApiError(error);
  if (apiError.requestId) {
    console.error(
      `[api] ${apiError.statusCode} ${apiError.code} (request-id: ${apiError.requestId})`,
    );
  }
}

export const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: (error, query) => {
      logRequestId(error);
      // Don't toast queries that nobody is observing yet — those are
      // typically background refetches the user never asked for.
      if (query.getObserversCount() === 0) return;
      if (!shouldToast(error, query.meta)) return;
      displayError(parseApiError(error).message);
    },
  }),
  mutationCache: new MutationCache({
    onError: (error, _vars, _context, mutation) => {
      logRequestId(error);
      if (!shouldToast(error, mutation.meta)) return;
      displayError(parseApiError(error).message);
    },
  }),
  defaultOptions: {
    queries: {
      staleTime: 60_000,
      gcTime: 5 * 60_000,
      refetchOnWindowFocus: false,
      retry: shouldRetry,
    },
    mutations: {
      retry: false,
    },
  },
});
